import React, {
  useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import classNames from 'classnames';
import { Button } from '@edx/paragon';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import track from 'react-tracking';
import { hasStarted } from '@utils/course';
import { addTrackingToLink, submitEvent } from '@utils/analytics';
import { getLmsLink, getAuthnMFELink, getLmsToCourseLink } from '@utils/edxLinks';
import { isCourseRunEnded } from '@utils/courseRunSelection';
import { WebviewContext } from '@utils/context';
import { enrollUser } from '@data/actions/enrollment';
import { withLocation } from '@utils/navigation';
import withUser from '@utils/withUser';
import courseRunShape from '@prospectus/common/types/courseRunShape';
import {
  setOnboardingComponentSignUpOpen,
} from '@data/actions/onboardingComponent';
import useInitOnboardingComponentExperiment from '@prospectus/common/hooks/useInitOnboardingComponentExperiment';
import tagularEvent from '@src/utils/cohesion/helpers/TagularEvent';
import { getCorrelationID, hyphenateForTagular } from '@src/utils/cohesion/helpers';
import { translateVariant } from '@src/utils/cohesion/helpers/dataTranslationMatrices';
import EnterpriseEnrollmentInformModal from '../EnterpriseEnrollmentInformModal';

function BaseEnrollButtonLink({
  buttonClass,
  buttonLabel,
  buttonVariant,
  children,
  className,
  courseRun: propCourseRun,
  defaultCourseRun,
  emailOptIn,
  enrollmentSubmissionStatus,
  enterpriseUser,
  hideAsSecondaryButton,
  makeButtonLight,
  onEnrollSubmitted,
  product,
  search,
  tracking,
  trackingID,
  userEnrollments,
  userEntitlements,
  userLoggedIn,
  uuid,
  owners,
  cohesionVariant,
  cohesionCategory,
  cohesionName,
}) {
  const dispatch = useDispatch();
  const [canRunOnboardingExperiment, setCanRunOnboardingExperiment] = useState(false);
  const [isEnrollButtonTracked, setIsEnrollButtonTracked] = useState(false);
  const {
    isUserInOnboardingExperimentVariation, isOnboardingComponentExperimentLoaded,
  } = useInitOnboardingComponentExperiment(canRunOnboardingExperiment);

  const [disabled, setDisabled] = useState(false);
  const [enrollmentSubmitted, setEnrollmentSubmitted] = useState(false);
  const [userEnrolled, setUserEnrolled] = useState(false);
  const [userEntitled, setUserEntitled] = useState(false);
  const [enterpriseInformModalState, setEnterpriseInformModalState] = useState(false);

  const isWebview = useContext(WebviewContext);

  const linkRef = useRef(null);

  const courseRun = useMemo(() => propCourseRun || defaultCourseRun, [propCourseRun, defaultCourseRun]);
  const courseStarted = useMemo(() => hasStarted(courseRun.start), [courseRun]);

  const generateCohesionPayload = () => {
    /** @type import("src/utils/cohesion/types/Tagular.ts").ConversionTracked */
    const conv = {
      correlation: {
        id: getCorrelationID(true),
      },
      metadata: {
        category: 'Enrollment',
        action: 'Started',
        timestamp: new Date().toISOString(),
        productList: [
          {
            brand: owners ? hyphenateForTagular(owners[0].name) : '',
            variant: translateVariant(cohesionVariant),
            category: hyphenateForTagular(cohesionCategory),
            name: cohesionName,
          },
        ],
      },
    };

    if (userEntitled) {
      // RV: Agreed upon with Data Team / MWoods
      conv.revenue = { actualRevenue: { amount: 0 } };
    }

    return conv;
  };

  const trackCohesionConversion = () => {
    if (!userEnrolled && cohesionVariant && cohesionCategory && cohesionName) {
      tagularEvent('ConversionTracked', generateCohesionPayload());
    }
  };

  const redirectUser = (url) => {
    window.location.href = url;
  };

  const getURIEncodedKey = () => encodeURIComponent(courseRun.key);

  const getTrackSelectionUrl = () => {
    const trackSelectionUrl = getLmsLink('/course_modes/choose/');

    return `${trackSelectionUrl}${getURIEncodedKey()}`;
  };

  const handleEnrollmentError = (response) => {
    // Current implementation from logistration
    const { jqXHR } = response;

    if (response && jqXHR && jqXHR.status === 403 && response.user_message_url) {
      /**
       * Check if we've been blocked from the course
       * because of country access rules.
       * If so, redirect to a page explaining to the user
       * why they were blocked.
       */
      const responseData = JSON.parse(jqXHR.responseText);
      redirectUser(responseData.user_message_url);
    // context is webview context which returns a boolean true if webview is active
    // only trigger redirect if it is not webview - redirect does not work on webview
    } else if (!isWebview) {
      /**
       * Otherwise, go to the track selection page as usual.
       * This can occur, for example, when a course does not
       * have a free enrollment mode, so we can't auto-enroll.
       */
      redirectUser(getTrackSelectionUrl());
    }
  };

  const getClickEventName = () => {
    const buttonID = trackingID ? `${trackingID}.` : '';

    return userEnrolled ? `edx.bi.link.enrollment.${buttonID}viewCourse` : `edx.bi.link.enrollment.${buttonID}clicked`;
  };

  const getLinkProps = () => {
    const {
      index,
      queryID,
      position,
    } = search;
    const link = linkRef.current && linkRef.current.getAttribute('href');
    const initialProps = {
      category: 'navigation',
      link,
      label: courseRun.key,
      user_enrolled: userEnrolled,
      user_entitled: userEntitled,
      user_logged_in: userLoggedIn,
      email_opt_in: emailOptIn,
      objectID: `course-${uuid}`,
    };
    let allProps = initialProps;
    // should either be just index or all three, in any case send all of them to indicate we came from a search page
    if (index || queryID || position) {
      allProps = {
        ...initialProps,
        index,
        queryID,
        position,
        products: [{ objectID: `course-${uuid}` }],
      };
    }
    return allProps;
  };

  const openEnterpriseEnrollmentModal = () => {
    const componentProps = {};
    submitEvent(tracking, componentProps, 'edx.bi.link.enterprise.enrollment.inform.modal.opened');
    setEnterpriseInformModalState(true);
  };

  const closeEnterpriseEnrollmentModal = () => {
    setEnterpriseInformModalState(false);
  };

  const enrollClickHandler = (event) => {
    const link = event.currentTarget.getAttribute('href');
    const componentProps = getLinkProps();

    const allComponentProps = {
      ...componentProps,
      link,
    };

    const eventName = getClickEventName();
    submitEvent(tracking, allComponentProps, eventName);
  };

  const trackEnroll = (event, trackClick) => {
    window.analytics.track(`edx.bi.user.${product}-details`, {
      category: `${product}-details`,
      label: courseRun.key,
    });
    trackCohesionConversion();
    if (trackClick) {
      enrollClickHandler(event);
    }
  };

  const enrollmentSubmittedButton = (event) => {
    setEnrollmentSubmitted(true);
    // button click needs explicit separate tracking for both button click and enrollment action
    trackEnroll(event, true);
  };

  const enroll = (event) => {
    setDisabled(true);

    if (!enrollmentSubmitted) {
      enrollmentSubmittedButton(event);

      onEnrollSubmitted({
        course_id: courseRun.key,
        email_opt_in: emailOptIn,
      });
    }
  };

  useEffect(() => {
    if (userLoggedIn && enrollmentSubmitted) {
      if (enrollmentSubmissionStatus.enrolled && !isWebview) {
        redirectUser(getTrackSelectionUrl());
      } else if (enrollmentSubmissionStatus.error) {
        handleEnrollmentError(enrollmentSubmissionStatus);
      }
    }
  }, [userLoggedIn, enrollmentSubmissionStatus]);

  useEffect(() => {
    const { key } = courseRun;
    const found = userEnrollments.some(({ courseId }) => courseId === key);

    setUserEnrolled(found);
  }, [userEnrollments, courseRun]);

  useEffect(() => {
    const found = userEntitlements.some(
      ({ courseUuid: userEntitledCourseUuid }) => userEntitledCourseUuid === uuid,
    );

    setUserEntitled(found);
  }, [userEntitlements, uuid]);

  const handleEnrollment = (event) => {
    // Making it explicit to enterprise learners
    // about how their enrollment will be created.
    if (enterpriseUser) {
      openEnterpriseEnrollmentModal();
    } else {
      enroll(event);
    }
  };

  const getWebviewEnrollmentUrl = () => `edxapp://enroll?course_id=${courseRun.key}&email_opt_in=${emailOptIn}`;

  const getWebviewDashboardUrl = () => `edxapp://enrolled_course_info?course_id=${courseRun.key}`;

  const enrollmentSubmittedLink = (event) => {
    setEnrollmentSubmitted(true);
    // pure <a> link already has click tracking on it, no need to track the click separately
    trackEnroll(event, false);
  };

  useEffect(() => {
    if (canRunOnboardingExperiment && isOnboardingComponentExperimentLoaded) {
      if (isUserInOnboardingExperimentVariation || (!isUserInOnboardingExperimentVariation && isEnrollButtonTracked)) {
        linkRef.current.click();
      }
    }
  }, [
    isOnboardingComponentExperimentLoaded,
    isEnrollButtonTracked,
  ]);

  useEffect(() => {
    if (isOnboardingComponentExperimentLoaded
      && !isUserInOnboardingExperimentVariation
    ) {
      addTrackingToLink(tracking, getClickEventName, linkRef.current, getLinkProps);
      setTimeout(() => {
        setIsEnrollButtonTracked(true);
      }, 300);
    }
  }, [
    canRunOnboardingExperiment,
    isOnboardingComponentExperimentLoaded,
    isUserInOnboardingExperimentVariation,
  ]);

  const handleNotLoggedInEnrollClick = (e) => {
    if (!isOnboardingComponentExperimentLoaded) {
      e.preventDefault();
      // manually trigger experiment
      setCanRunOnboardingExperiment(true);
    } else {
      if (isOnboardingComponentExperimentLoaded && isUserInOnboardingExperimentVariation) {
        e.preventDefault();
        dispatch(setOnboardingComponentSignUpOpen({
          course_id: courseRun.key,
          enrollment_action: 'enroll',
          email_opt_in: emailOptIn,
        }));
        window.analytics.track(getClickEventName(), getLinkProps());
        trackCohesionConversion();
      }
      enrollmentSubmittedLink(e);
    }
  };

  const { isEnrollable: courseIsEnrollable } = courseRun;

  const courseIsArchived = isCourseRunEnded(courseRun.end);
  const hideButton = hideAsSecondaryButton && (!courseIsEnrollable || courseIsArchived || userEnrolled);

  if (!hideButton) {
    return (
      <WebviewContext.Consumer>
        {(webview) => (
          <div className={className}>
            {webview && courseIsEnrollable
              && (
                <>
                  {!userEnrolled && (
                    <a
                      href={getWebviewEnrollmentUrl()}
                      className={classNames(
                        `btn btn-${makeButtonLight ? 'outline-brand' : buttonVariant}`,
                        buttonClass,
                      )}
                      onClick={enrollmentSubmittedButton}
                    >{children}
                    </a>
                  )}
                  {userEnrolled && (
                    <a
                      href={getWebviewDashboardUrl()}
                      className={classNames(`btn btn-${buttonVariant}`, buttonClass)}
                      onClick={enrollmentSubmittedButton}
                    >{children}
                    </a>
                  )}
                </>
              )}
            {!webview
              && (
                <>
                  {!userLoggedIn && courseIsEnrollable
                  && (
                    <a
                      href={getAuthnMFELink(`/register?course_id=${getURIEncodedKey()}&enrollment_action=enroll&email_opt_in=${emailOptIn}`)}
                      className={classNames(`btn btn-${buttonVariant}`, buttonClass)}
                      onClick={handleNotLoggedInEnrollClick}
                      ref={linkRef}
                    >{children}
                    </a>
                  )}
                  {userLoggedIn && !userEnrolled && courseIsEnrollable
                  && (
                    <Button
                      variant={`${makeButtonLight ? 'outline-brand' : buttonVariant}`}
                      className={buttonClass}
                      onClick={handleEnrollment}
                      label={buttonLabel}
                      disabled={disabled}
                      id={courseRun.key}
                    >{children}
                    </Button>
                  )}
                  {userLoggedIn && userEnrolled
                  && (
                    <a
                      href={courseStarted
                        ? getLmsToCourseLink(`/course/${getURIEncodedKey()}/home`)
                        : getLmsLink('/dashboard')}
                      className={classNames(`btn btn-${buttonVariant}`, buttonClass)}
                      ref={linkRef}
                      onClick={enrollmentSubmittedLink}
                    >{children}
                    </a>
                  )}
                  <EnterpriseEnrollmentInformModal
                    isOpen={enterpriseInformModalState}
                    close={closeEnterpriseEnrollmentModal}
                    enrollFlow={(e) => enroll(e)}
                  />
                </>
              )}
            {(!userEnrolled && !courseIsEnrollable)
              && <div className="alert alert-secondary text-center">{children}</div>}
          </div>
        )}
      </WebviewContext.Consumer>
    );
  }

  return null;
}

BaseEnrollButtonLink.propTypes = {
  defaultCourseRun: PropTypes.shape(courseRunShape).isRequired,
  courseRun: PropTypes.shape(courseRunShape),
  onEnrollSubmitted: PropTypes.func.isRequired,
  enrollmentSubmissionStatus: PropTypes.shape({
    enrolled: PropTypes.bool,
    error: PropTypes.bool,
    value: PropTypes.string,
  }).isRequired,
  userEnrollments: PropTypes.arrayOf(PropTypes.shape({
    courseId: PropTypes.string.isRequired,
    mode: PropTypes.string.isRequired,
  })),
  userEntitlements: PropTypes.arrayOf(PropTypes.shape({
    courseUuid: PropTypes.string.isRequired,
  })),
  userLoggedIn: PropTypes.bool,
  search: PropTypes.shape({
    index: PropTypes.number,
    queryID: PropTypes.string,
    position: PropTypes.number,
  }),
  tracking: PropTypes.shape().isRequired,
  emailOptIn: PropTypes.bool,
  product: PropTypes.string,
  uuid: PropTypes.string.isRequired,
  buttonClass: PropTypes.string,
  buttonVariant: PropTypes.string,
  hideAsSecondaryButton: PropTypes.bool,
  trackingID: PropTypes.string,
  buttonLabel: PropTypes.string.isRequired,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  makeButtonLight: PropTypes.bool,
  enterpriseUser: PropTypes.bool,
  owners: PropTypes.arrayOf(
    PropTypes.shape({
      uuid: PropTypes.string,
      name: PropTypes.string,
    }),
  ).isRequired,
  cohesionVariant: PropTypes.string,
  cohesionCategory: PropTypes.string,
  cohesionName: PropTypes.string,
};

BaseEnrollButtonLink.defaultProps = {
  userEnrollments: [],
  userEntitlements: [],
  userLoggedIn: false,
  emailOptIn: false,
  product: 'course',
  buttonClass: '',
  buttonVariant: 'brand',
  hideAsSecondaryButton: false,
  search: {},
  trackingID: undefined,
  courseRun: null,
  className: '',
  makeButtonLight: false,
  enterpriseUser: false,
  cohesionVariant: null,
  cohesionCategory: null,
  cohesionName: null,
};

const mapStateToProps = store => ({
  enrollmentSubmissionStatus: store.enrollment,
  userLoggedIn: store.userLoggedIn,
  defaultCourseRun: store.activeCourseRun,
});

const mapDispatchToProps = dispatch => ({
  onEnrollSubmitted(data) {
    dispatch(enrollUser(data));
  },
});

export default track(
  { component: 'enroll-button-link' },
)(connect(mapStateToProps, mapDispatchToProps)(withUser(withLocation(BaseEnrollButtonLink))));
